/**
 * AgentPanelPage — the /agent-panel route rendered inside the Collabora
 * editor's #zehn-agent-dock iframe (injected by the fork's branding.js).
 *
 * Reads the file identity from query params (?fileId=&title=&lang=, posted
 * down by the host in Zehn_AgentContext as the panelUrl) and hosts DockPanel:
 * zehn-aip's assistant and the template's Placeholders.
 * Any file opened in an edit session gets the dock — not only some product's
 * documents.
 * Outbound events go up to the collabora frame as Zehn_PanelEvent envelopes;
 * branding.js relays them verbatim to the WOPI host with a locked origin.
 *
 * The request/response protocol with branding.js (the assistant's editor
 * operations, template placeholder bookmarks) lives in hooks/useEditorBridge.js
 * and is handed to DockPanel as `editorBridge`.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DockPanel } from '../components/DockPanel.jsx';
import { postPanelEvent, useEditorBridge } from '../hooks/useEditorBridge.js';

export function AgentPanelPage() {
  const { t } = useTranslation('assistant');
  const [searchParams] = useSearchParams();
  const fileId = searchParams.get('fileId');
  const title = searchParams.get('title') || '';

  const file = useMemo(
    () => (fileId ? { id: Number(fileId) || fileId, title } : null),
    [fileId, title],
  );

  const editorBridge = useEditorBridge();

  const handleDocumentChanged = useCallback(() => {
    postPanelEvent({ type: 'document_changed', fileId: file?.id ?? null });
  }, [file]);

  const handleCollapse = useCallback(() => {
    postPanelEvent({ type: 'close_panel' });
  }, []);

  if (!file) {
    return (
      <div className="flex h-screen items-center justify-center bg-white text-xs text-muted-foreground">
        {t('agent.missingContext')}
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <DockPanel
        file={file}
        editorBridge={editorBridge}
        onDocumentChanged={handleDocumentChanged}
        onCollapse={handleCollapse}
      />
    </div>
  );
}
